import { Box, Typography, Stack, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import {
  READ_EMPLOYEE_FAMILY_INFORMATION,
  UPDATE_EMPLOYEE_FAMILY_INFORMATION,
  useIsMyProfile,
} from 'domain/shared/employee';
import { ShowSection, FormSection } from 'framework/ui/layout';
import { useCallback, useState } from 'react';
import { useFormShowContext, useFormContext } from 'framework/form';
import EditFormShowButton from '../../../shared/button/showEdit/EditFormShowButton';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import FamilyMemberItem from './FamilyMemberItem';
import { useFamilyData, createNewFamilyMember } from './useFamilyData';
import { FamilyMember, FamilyMemberType } from './types';
import FormActionButtons from '../../../shared/button/showEdit/FormActionButtons';
import PrepopulateButton from 'domain/people/employee/prepopulation/PrepopulateButton';
import { usePrepopulationContext } from 'domain/people/employee/prepopulation/PrepopulationContext';
import FamilyPrepopulateModal from '../createupdate/FamilyPrepopulateModal';

const FamilyContent = () => {
  const { data: employee, setData } = useShowContext();
  const { isGranted } = useAuthorization();
  const { isEditMode } = useFormShowContext();
  const { data, setFormData } = useFormContext();
  const isMyProfile = useIsMyProfile();
  const { children, other } = useFamilyData(isEditMode, data, employee);
  const { forms = [] } = usePrepopulationContext();
  const [isPrepopulateModalOpen, setIsPrepopulateModalOpen] = useState(false);

  const removeFamilyMember = useCallback(
    (index: number, type: FamilyMemberType) => {
      if (!setFormData || !data) return;
      const updated = (data.extendedInformation?.[type] || []).filter((_: unknown, i: number) => i !== index);
      setFormData({
        ...data,
        extendedInformation: { ...data.extendedInformation, [type]: updated },
      });
    },
    [data, setFormData],
  );

  if (!isGranted(READ_EMPLOYEE_FAMILY_INFORMATION)) return null;

  const addFamilyMember = (type: FamilyMemberType) => {
    if (!setFormData || !data) return;
    const newMember = createNewFamilyMember();
    const updated = [...(data.extendedInformation?.[type] || []), newMember];
    setFormData({
      ...data,
      extendedInformation: { ...data.extendedInformation, [type]: updated },
    });
  };

  const getEditActions = () => {
    if (isMyProfile || !isGranted(UPDATE_EMPLOYEE_FAMILY_INFORMATION)) return null;
    return isEditMode ? (
      <FormActionButtons />
    ) : (
      <Box display="flex">
        {hasPrepopulationForms && (
          <PrepopulateButton
            scrollToPrepopulatedInputs={() => setIsPrepopulateModalOpen(true)}
            sx={{ mt: 0.5, mr: 1 }}
          />
        )}
        <EditFormShowButton requiredPermission={UPDATE_EMPLOYEE_FAMILY_INFORMATION} />
      </Box>
    );
  };

  const allMembers = [
    ...children.map((member: FamilyMember, i: number) => (
      <FamilyMemberItem
        key={`children-${member.localId}`}
        member={member}
        index={i}
        type="children"
        isEditMode={isEditMode}
        removeFamilyMember={removeFamilyMember}
      />
    )),
    ...other.map((member: FamilyMember, i: number) => (
      <FamilyMemberItem
        key={`other-${member.localId}`}
        member={member}
        index={i}
        type="other"
        isEditMode={isEditMode}
        removeFamilyMember={removeFamilyMember}
      />
    )),
  ];

  const hasPrepopulationForms = forms.length > 0 && allMembers.length === 0;

  return (
    <ShowSection
      sx={{
        position: 'relative',
        '.show-title-container': {
          marginBottom: '20px',
        },
      }}
      title={
        <FamilyRestroomIcon
          sx={{
            color: 'text.primary',
          }}
        />
      }
      actions={
        <Box
          sx={{
            position: 'absolute',
            right: '-15px',
            top: '-4px',
            button: {
              m: 0,
            },
          }}
        >
          {getEditActions()}
        </Box>
      }
      disableDefaultPadding
    >
      {isEditMode ? (
        <FormSection title="" formStyle={{ padding: 0, border: 'none', boxShadow: 'none', outline: 'none' }}>
          {allMembers.length === 0 && (
            <Typography variant="body1" color="textSecondary" p={1}>
              No family members
            </Typography>
          )}
          <Stack spacing={2}>{allMembers}</Stack>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => addFamilyMember('other')} size="small">
              Add family member
            </Button>
          </Box>
        </FormSection>
      ) : (
        <Box>
          {allMembers.length === 0 ? (
            <Typography variant="body1" color="textSecondary" pl={2}>
              No family members
            </Typography>
          ) : (
            <>
              <Stack spacing={2}>{allMembers}</Stack>
            </>
          )}
          <FamilyPrepopulateModal
            isOpen={isPrepopulateModalOpen}
            onClose={() => setIsPrepopulateModalOpen(false)}
            onSave={(updatedData?: any) => {
              if (setData && updatedData) {
                setData(updatedData);
              }
              setIsPrepopulateModalOpen(false);
            }}
          />
        </Box>
      )}
    </ShowSection>
  );
};

export default FamilyContent;
